import React, { useEffect, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import { Box, InputAdornment, Stack } from '@mui/material';
import Decimal from 'decimal.js';

import AcInput from '../AcInput/AcInput';

import { CustomPricing } from './Pricing.types';

const PricingTableInputCell = ({
  pricePoint,
  onPriceUpdate,
  disabled = false,
  customPriceData
}: {
  pricePoint: number;
  onPriceUpdate: (price: Decimal, currencyCode: string) => void;
  customPriceData: CustomPricing;
  disabled?: boolean;
}) => {
  const [isChangeable, setIsChangeable] = useState(false);
  const [updatedPricePoint, setUpdatedPricePoint] = useState(pricePoint);
  const [maxPrice, setMaxPrice] = useState<number>(0);
  const [minPrice, setMinPrice] = useState<number>(0);
  const [priceValue, setPriceValue] = useState(pricePoint);
  const ref = React.createRef();

  const calculateMinMaxPrices = (
    customPriceData: CustomPricing
  ): Record<string, number> => {
    const maxPrice = new Decimal(customPriceData.maxChargeAmount).div(
      customPriceData.multiplier
    );
    const minPrice = new Decimal(customPriceData.minChargeAmount).div(
      customPriceData.multiplier
    );
    return {
      maxPrice: Number(maxPrice.toFixed(2)),
      minPrice: Number(minPrice.toFixed(2))
    };
  };

  useEffect(() => {
    if (isChangeable && ref.current) {
      (ref.current as HTMLElement).querySelector('input')?.focus();
    }
  }, [isChangeable, ref]);

  useEffect(() => {
    setUpdatedPricePoint(pricePoint);
  }, [pricePoint]);

  useEffect(() => {
    const { maxPrice, minPrice } = calculateMinMaxPrices(customPriceData);
    setMaxPrice(maxPrice);
    setMinPrice(minPrice);
  }, [customPriceData]);

  return (
    <>
      {isChangeable ? (
        <Stack onClick={() => setIsChangeable(false)} mt={-1.25}>
          <AcInput
            ref={ref}
            size="small"
            type="number"
            variant="standard"
            helperText={
              priceValue < minPrice
                ? `Price must be larger than ${minPrice}`
                : priceValue > maxPrice
                  ? `Price must be smaller than ${maxPrice}`
                  : ''
            }
            error={priceValue < customPriceData.minChargeAmount}
            slotProps={{
              htmlInput: {
                min: minPrice,
                max: maxPrice,
                style: {
                  fontSize: '14px'
                }
              },
              input: {
                startAdornment: (
                  <InputAdornment
                    position="start"
                    style={{
                      marginRight:
                        customPriceData.currencySymbol.length >= 3
                          ? '28px'
                          : customPriceData.currencySymbol.length === 2
                            ? '20px'
                            : '8px'
                    }}
                  >
                    {customPriceData.currencySymbol}
                  </InputAdornment>
                )
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              if (
                customPriceData.multiplier === 1 &&
                (e.key === '.' || e.key === ',')
              ) {
                e.preventDefault();
                return;
              }
              const newPrice = parseFloat((e.target as HTMLInputElement).value);
              if (
                e.key === 'Enter' &&
                newPrice >= minPrice &&
                newPrice <= maxPrice
              ) {
                setIsChangeable(false);
                onPriceUpdate(
                  new Decimal((e.target as HTMLInputElement).value).times(
                    new Decimal(customPriceData.multiplier)
                  ),
                  customPriceData.countryCode3
                );
              }
              e.stopPropagation();
            }}
            onKeyUp={(e) => {
              const newPrice = (e.target as HTMLInputElement).value;
              setPriceValue(parseFloat(newPrice));
            }}
            onBlur={(e) => {
              const newPrice = (e.target as HTMLInputElement).value;
              e.target.value &&
                parseFloat(newPrice) >= minPrice &&
                parseFloat(newPrice) <= maxPrice &&
                onPriceUpdate(
                  new Decimal((e.target as HTMLInputElement).value).times(
                    new Decimal(customPriceData.multiplier)
                  ),
                  customPriceData.countryCode3
                );
              setIsChangeable(false);
            }}
          />
        </Stack>
      ) : (
        <Stack
          onClick={() => !disabled && setIsChangeable(true)}
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1
          }}
          width="100%"
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          {`${customPriceData.currencySymbol}
    ${
      updatedPricePoint
        ? customPriceData.multiplier === 1
          ? updatedPricePoint.toFixed(0)
          : updatedPricePoint.toFixed(2)
        : 0
    }`}
          <Box ml={1}>
            <EditIcon
              sx={{
                color: 'gray',
                fontSize: '14px'
              }}
            />
          </Box>
        </Stack>
      )}
    </>
  );
};

export default PricingTableInputCell;
